import React, { useEffect, useState } from "react"
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Stack, Tab, Tabs, Typography
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import VersionInfo from "./VersionInfo"
import { getGroupApplications } from "../GroupApplications"
import AssignedApplicationsTable from "./AssignedApplicationsTable"

const display = (value) => value === null || value === undefined || value === "" ? "Not available" : String(value)
const humanize = (value) => value ? String(value).replace(/_/g, " ").toLowerCase().replace(/^./, (letter) => letter.toUpperCase()) : "Not available"

const DetailSection = ({ title, fields, columns = 2, children }) => <Paper component="section" aria-label={title} variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: "#dce7e7", minWidth: 0 }}>
  <Typography component="h3" variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: "#234d50" }}>{title}</Typography>
  <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: { xs: "1fr", sm: `repeat(${columns}, minmax(0, 1fr))` }, gap: 2 }}>
    {fields.map(([label, value]) => <Box key={label}>
      <Typography component="dt" variant="caption" sx={{ color: "#61747c" }}>{label}</Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0, mt: 0.5, color: "#233e48", fontWeight: 500, overflowWrap: "anywhere" }}>{display(value)}</Typography>
    </Box>)}
  </Box>
  {children}
</Paper>

const ApproveGrpMoreInfoModal = (props) => {
  const [tab, setTab] = useState(0)
  const group = props.grpData || {}
  const summary = group.groupSummaryDtl || props.groupSummaryDtl || {}
  const disconnectedTerminals = summary.totTerminals != null && summary.connectedTerminals != null
    ? summary.totTerminals - summary.connectedTerminals : null
  const applications = getGroupApplications({
    latestApplications: group.latestApplications ?? props.latestApplications,
    latestApplication: group.latestApplication ?? props.latestAppDtl,
  })
  useEffect(() => { setTab(0) }, [props.open, group.groupId])
  const statusColor = { COMPLETED: "success", FAILED: "error", STOP: "error", RETRY: "warning", IN_PROGRESS: "info", START: "info" }[group.deployStatus] || "default"

  return <Dialog fullWidth maxWidth="lg" open={props.open} onClose={props.onClose}
    aria-labelledby="group-details-title" PaperProps={{ sx: { borderRadius: "10px", maxHeight: "90vh" } }}>
    <DialogTitle id="group-details-title" sx={{ bgcolor: "#116c6f", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography component="span" variant="h6" sx={{ display: "block", overflowWrap: "anywhere" }}>{group.groupName || "Group details"}</Typography>
        <Typography component="span" variant="body2" sx={{ opacity: 0.85 }}>Organization: {display(group.orgCode)}</Typography>
      </Box>
      <IconButton aria-label="Close group details" onClick={props.onClose} sx={{ color: "inherit" }}><CloseIcon /></IconButton>
    </DialogTitle>
    <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto" aria-label="Group details" sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
      {["Overview", `Assigned applications (${applications.length})`, "Version history"].map((label, index) =>
        <Tab key={index} label={label} id={`group-details-tab-${index}`} aria-controls={`group-details-panel-${index}`} />)}
    </Tabs>
    <Box role="tabpanel" id={`group-details-panel-${tab}`} aria-labelledby={`group-details-tab-${tab}`} sx={{ overflowY: "auto", minHeight: 0 }}>
      {tab === 0 && <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f5f8fa", overflow: "visible" }}>
        <Stack spacing={2.5}>
          <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 2 }}>
            {[
              ["Total terminals", summary.totTerminals, "#164f51", "#f0f7f6"],
              ["Active terminals", summary.activeTerminals, "#164f51", "#f0f7f6"],
              ["Connected terminals", summary.connectedTerminals, "#237a4a", "#eff8f2"],
              ["Disconnected terminals", disconnectedTerminals, "#b34843", "#fff5f4"],
            ].map(([label, value, color, background]) =>
              <Paper key={label} variant="outlined" sx={{ p: 2, minWidth: 0, borderRadius: 3, borderColor: "#dce7e7", bgcolor: background, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <Typography component="dt" variant="body2" sx={{ color }}>{label}</Typography>
                <Typography component="dd" sx={{ m: 0, fontSize: value == null ? 14 : 28, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>{value ?? "Not reported"}</Typography>
              </Paper>)}
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 2.5 }}>
            <DetailSection title="Group information" fields={[
              ["Group name", group.groupName], ["Organization code", group.orgCode],
            ]}>
              <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #edf1f3" }}>
                <Typography component="h4" variant="caption" sx={{ color: "#61747c", mb: 0.5 }}>Description</Typography>
                <Typography variant="body2" sx={{ m: 0, color: "#233e48", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{group.description || "No description provided."}</Typography>
              </Box>
            </DetailSection>
            <DetailSection title="Deployment settings" fields={[
              ["Update strategy", humanize(group.deployStgy)],
              ["Settlement", humanize(group.settlementStng)],
            ]}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap flexWrap="wrap" gap={1} sx={{ mt: 2, pt: 2, borderTop: "1px solid #edf1f3" }}>
                <Typography variant="body2" sx={{ color: "#61747c" }}>Deployment status</Typography>
                <Chip size="small" label={humanize(group.deployStatus)} color={statusColor} sx={{ maxWidth: "100%", height: "auto", minHeight: 24, "& .MuiChip-label": { whiteSpace: "normal", py: 0.25 } }} />
              </Stack>
            </DetailSection>
          </Box>
          <DetailSection title="Activity" columns={3} fields={[
            ["Created", group.createDateTime ?? group.createdTime],
            ["Last updated", group.updatedDateTime ?? group.updatedTime],
            ["Update on", group.updatedOnDateTime ?? group.updatedOn],
          ]} />
        </Stack>
      </DialogContent>}
      {tab === 1 && <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f5f8fa", overflow: "visible" }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Applications currently assigned to this group. Installation counts are shown separately for each application.</Typography>
        <AssignedApplicationsTable applications={applications} />
      </DialogContent>}
      {tab === 2 && props.open && <VersionInfo key={group.groupId} onClose={props.onClose} grpData={group} grpId={group.groupId} />}
    </Box>
    {tab !== 2 && <DialogActions sx={{ p: "1.25rem", borderTop: 1, borderColor: "divider" }}>
      <Button onClick={props.onClose} variant="outlined" color="error">Close</Button>
    </DialogActions>}
  </Dialog>
}

export default ApproveGrpMoreInfoModal
